import * as React from 'react';
import { useMemo } from 'react';
import { FormControlLabel, Checkbox } from '@mui/material';
import cn from 'classnames';

import { Money as MoneyType } from '../../../Money/types';
import { useTheme } from '../../../theme';
import { Switch } from '@shared/ui';
import { Money } from '../../../Money';
import Counter from '../../../Counter/Counter';

export interface Props {
	type: 'switch' | 'checkbox' | 'counter';
	count: number;
	price?: MoneyType;
	disabled?: boolean;
	label: React.ReactNode;
	maxCount?: number;
	onSetCount: (count: number) => void;
}

const MIN_COUNTER_VALUE = 0;

const Control: React.FC<Props> = ({ type, count, price, disabled, label, maxCount = 7, onSetCount }) => {
	const { VipServiceSelect: theme } = useTheme('VipServiceSelect');

	const onChange = React.useCallback(() => {
		onSetCount(count > 0 ? 0 : 1);
	}, [count, onSetCount]);

	const onCounterIncrease = React.useCallback(() => {
		if (count + 1 <= maxCount) {
			onSetCount(count + 1);
		}
	}, [count, maxCount, onSetCount]);

	const onCounterDecrease = React.useCallback(() => {
		if (count - 1 >= MIN_COUNTER_VALUE) {
			onSetCount(count - 1);
		}
	}, [count, onSetCount]);

	const control = useMemo(
		() =>
			({
				switch: (
					<Switch
						classes={{ root: theme.control__switch_root }}
						checked={count > 0}
						onChange={onChange}
						label={label}
						disabled={disabled}
						dataTestId="vip-service-control"
						dataControlType="switch"
					/>
				),
				checkbox: (
					<FormControlLabel
						value="end"
						label={label}
						data-test-id="vip-service-control"
						data-control-type="checkbox"
						classes={{
							root: theme.checkbox__root,
							label: theme.checkbox_label
						}}
						control={
							<Checkbox
								classes={{
									root: theme.checkbox,
									checked: theme.checkbox_checked
								}}
								checked={count > 0}
								disabled={disabled}
								onChange={onChange}
							/>
						}
					/>
				),
				counter: (
					<div
						className={theme.control__label}
						data-test-id="vip-service-control"
						data-control-type="counter"
					>
						{label}

						<Counter
							value={count}
							maxValue={maxCount}
							minValue={MIN_COUNTER_VALUE}
							onIncrease={onCounterIncrease}
							onDecrease={onCounterDecrease}
							showOnlyIncreaseButtonWhenValueIsNull={true}
							readonly={disabled}
							classes={{
								root: theme.counter__root,
								minus: theme.counter,
								plus: theme.counter,
								value: theme.counter__value
							}}
						/>
					</div>
				)
			}[type]),
		[type, disabled, label, count, maxCount, onChange, onCounterIncrease, onCounterDecrease]
	);

	return (
		<div
			className={cn(theme.control, {
				[theme.control_switch]: type === 'switch',
				[theme.control_checkbox]: type === 'checkbox',
				[theme.control_counter]: type === 'counter',
				[theme.control_selected]: count > 0
			})}
		>
			{control}
			{price?.amount !== undefined && (
				<div className={theme.control__price}>
					<Money money={price} />
				</div>
			)}
		</div>
	);
};

export default React.memo(Control);
